import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { primaryDebtor, secondaryFilingDebtor } from '../../utils/debtors';

// Alias type dropdown options
const ALIAS_TYPE_CHOICES = [
  { value: 'fka', label: 'FKA' },
  { value: 'aka', label: 'AKA' },
  { value: 'dba', label: 'DBA' },
  { value: 'fdba', label: 'FDBA' },
  { value: 'pdba', label: 'PDBA' },
  { value: 'pka', label: 'PKA' },
  { value: 'ta', label: 'TA' },
];

// Simple street parser: house number, street name, street type
function parseStreet(street = '') {
  const parts = street.trim().split(/\s+/);
  if (parts.length < 2) {
    return { houseNumber: '', streetName: street, streetType: '' };
  }
  const houseNumber = parts[0];
  const streetType = parts[parts.length - 1];
  const streetName = parts.slice(1, parts.length - 1).join(' ');
  return { houseNumber, streetName, streetType };
}
const API_URL = process.env.REACT_APP_API_URL;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
});

const pendingKey = caseId => `oneview-pending-report:${caseId}`;

function restoredRequest(caseId) {
  try {
    const saved = JSON.parse(sessionStorage.getItem(pendingKey(caseId)) || 'null');
    if (saved && /^[a-f0-9-]{36}$/i.test(saved.id) && ['primary', 'joint', 'both'].includes(saved.scope)) return saved;
  } catch { /* Request tracking still works in memory when storage is unavailable. */ }
  return null;
}

function newRequestId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  if (!window.crypto?.getRandomValues) throw new Error('This browser cannot safely identify a new report request. Open this page in an up-to-date browser over HTTPS and try again.');
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

async function responseData(response, fallback) {
  let data;
  try {
    data = await response.json();
  } catch {
    throw new Error(fallback);
  }
  if (!response.ok) throw new Error(data?.detail || fallback);
  return data;
}

function personName(person) {
  return [person?.firstName, person?.middleName, person?.lastName].filter(Boolean).join(' ');
}

function reportDate(value) {
  if (!value) return 'Date unavailable';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Date unavailable' : date.toLocaleString();
}

export default function FirmOneView() {
  const { caseId } = useParams();
  const [loading, setLoading] = useState(true);
  const emptyPerson = {
    id: null,
    firstName: '',
    middleName: '',
    lastName: '',
    previousNames: [], // array of { first, middle, last, aliasType }
    ssn: '',
    ssnConfirm: '',
    address: { houseNumber: '', streetName: '', streetType: '', city: '', state: '', zip: '' },
  };

  const [primary, setPrimary] = useState({ ...emptyPerson });
  const [joint, setJoint] = useState(null);
  const [reports, setReports] = useState([]);       // existing pulled reports
  const [reportsLoading, setReportsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [reportsError, setReportsError] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [freshScope, setFreshScope] = useState('primary');
  const [freshReview, setFreshReview] = useState(null);
  const [pendingFresh, setPendingFresh] = useState(null);
  const inFlight = useRef(false);
  const savedPeople = useRef({ primary: null, joint: null });

  const rememberPending = request => {
    setPendingFresh(request);
    try {
      if (request) sessionStorage.setItem(pendingKey(caseId), JSON.stringify({ id: request.id, scope: request.scope }));
      else sessionStorage.removeItem(pendingKey(caseId));
    } catch { /* Never store names, SSNs, addresses, or report contents here. */ }
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError('');
    setFreshReview(null);
    setPendingFresh(restoredRequest(caseId));
    fetch(`${API_URL}/case/${caseId}/client-info-oneview/`, {
      credentials: 'include',
      headers: authHeaders(),
    })
      .then(res => responseData(res, 'Could not load debtor information. Please reload this page before requesting a report.'))
      .then(data => {
        if (!active) return;
        // 'data' is now an array of debtor objects
        // Always show the primary debtor first
        const primaryData = primaryDebtor(data, { filingOnly: true }) || {};
        // Only include the joint debtor if they exist and are also filing
        const jointDataRaw = secondaryFilingDebtor(data);
        const jointData = jointDataRaw ? jointDataRaw : null;

        // Helper to map API object to form state
        const mapToPerson = apiObj => {
          const addr = parseStreet(apiObj.address1 || '');
          const initialSsn = apiObj.ssn || '';
          return {
            id: apiObj.id || null,
            firstName: apiObj.first_name || '',
            middleName: apiObj.middle_name || '',
            lastName: apiObj.last_name || '',
            previousNames: (apiObj.aliases || []).map(alias => ({
              first: alias.first_name || '',
              middle: alias.middle_name || '',
              last: alias.last_name || '',
              aliasType: alias.alias_type || 'aka',
            })),
            ssn: initialSsn,
            ssnConfirm: initialSsn,
            address: {
              ...addr,
              city: apiObj.city || '',
              state: apiObj.state || '',
              zip: apiObj.postal_code || '',
            },
          };
        };

        // Populate primary form
        const loadedPrimary = mapToPerson(primaryData);
        const loadedJoint = jointData ? mapToPerson(jointData) : null;
        setPrimary(loadedPrimary);
        setJoint(loadedJoint);
        savedPeople.current = { primary: loadedPrimary, joint: loadedJoint };
        setFreshScope(loadedJoint ? 'both' : 'primary');
      })
      .catch(error => { if (active) setLoadError(error.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [caseId]);

  const loadReports = useCallback(async () => {
    setReportsLoading(true);
    setReportsError('');
    try {
      const response = await fetch(`${API_URL}/case/${caseId}/oneview-download/`, {
        credentials: 'include',
        headers: authHeaders(),
      });
      const data = await responseData(response, 'Could not load saved reports.');
      if (!Array.isArray(data)) throw new Error('Could not load saved reports.');
      setReports(data);
    } catch (error) {
      // Keep already displayed downloads when a refresh fails.
      setReportsError(`${error.message} Existing reports have not been removed.`);
    } finally {
      setReportsLoading(false);
    }
  }, [caseId]);

  useEffect(() => { loadReports(); }, [loadReports]);

  // Handlers
  const handlePersonChange = (personKey, field) => e => {
    const setter = personKey === 'primary' ? setPrimary : setJoint;
    const person = personKey === 'primary' ? primary : joint;
    setter({ ...person, [field]: e.target.value });
  };
  const handleAddressChange = (personKey, field) => e => {
    const setter = personKey === 'primary' ? setPrimary : setJoint;
    const person = personKey === 'primary' ? primary : joint;
    setter({
      ...person,
      address: { ...person.address, [field]: e.target.value },
    });
  };
  const handleAliasChange = (personKey, idx, field) => e => {
    const setter = personKey === 'primary' ? setPrimary : setJoint;
    const person = personKey === 'primary' ? primary : joint;
    const aliases = [...person.previousNames];
    aliases[idx] = { ...aliases[idx], [field]: e.target.value };
    setter({ ...person, previousNames: aliases });
  };
  const addAlias = personKey => {
    const setter = personKey === 'primary' ? setPrimary : setJoint;
    const person = personKey === 'primary' ? primary : joint;
    setter({
      ...person,
      previousNames: [...person.previousNames, { first: '', middle: '', last: '', aliasType: 'aka' }],
    });
  };
  const removeAlias = (personKey, idx) => {
    const setter = personKey === 'primary' ? setPrimary : setJoint;
    const person = personKey === 'primary' ? primary : joint;
    const aliases = person.previousNames.filter((_, i) => i !== idx);
    setter({ ...person, previousNames: aliases });
  };

  // Format a string of digits as XXX-XX-XXXX
  function formatSSN(value) {
    const digits = value.replace(/\D/g, '').slice(0, 9);
    if (digits.length <= 3) return digits;
    if (digits.length <= 5) return `${digits.slice(0,3)}-${digits.slice(3)}`;
    return `${digits.slice(0,3)}-${digits.slice(3,5)}-${digits.slice(5)}`;
  }

  // Handler to apply SSN formatting and update state
  const handleSSNChange = (personKey) => (e) => {
    const setter = personKey === 'primary' ? setPrimary : setJoint;
    const person = personKey === 'primary' ? primary : joint;
    const formatted = formatSSN(e.target.value);
    setter({ ...person, ssn: formatted });
  };

  // Handler to update SSN confirmation field
  const handleSSNConfirmChange = (personKey) => (e) => {
    const setter = personKey === 'primary' ? setPrimary : setJoint;
    const person = personKey === 'primary' ? primary : joint;
    setter({ ...person, ssnConfirm: e.target.value });
  };

  const runReport = async ({ payload, saveInformation = false, freshRequest = null }) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setIsSubmitting(true);
    setFeedback(null);
    setFreshReview(null);
    if (freshRequest) rememberPending(freshRequest);
    try {
      if (saveInformation) {
        const updateResponse = await fetch(`${API_URL}/case/${caseId}/client-info-oneview/`, {
          method: 'PUT',
          credentials: 'include',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        await responseData(updateResponse, 'Failed to update debtor information. No report was requested.');
        savedPeople.current = { primary, joint };
      }
      const response = await fetch(`${API_URL}/case/${caseId}/oneview/`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await responseData(response, 'Could not confirm the report request. Please retry the same request rather than starting another new report.');
      const results = Array.isArray(data) ? data : data?.results;
      if (!Array.isArray(results)) throw new Error('Could not confirm the report request. Please retry the same request rather than starting another new report.');
      if (freshRequest && !results.length) throw new Error('Could not confirm the report request. Please retry the same request rather than starting another new report.');
      if (freshRequest && Object.keys(freshRequest.payload).some(role =>
        results.filter(item => item.role === role && ['success', 'error', 'pending', 'failed', 'review_required'].includes(item.status)).length !== 1,
      )) throw new Error('The response did not confirm every selected debtor. Check/retry the same request before authorizing another report.');
      const failures = results.filter(item => ['error', 'failed', 'review_required'].includes(item.status));
      const pending = results.filter(item => item.status === 'pending');
      const completed = results.filter(item => ['success', 'cached'].includes(item.status));
      setFeedback({
        error: failures.length > 0,
        message: failures.length || pending.length
          ? `${completed.length ? 'Some reports completed; ' : ''}${[...failures, ...pending].map(item => `${item.role === 'joint' ? 'Debtor 2' : 'Debtor 1'}: ${item.detail || (item.status === 'pending' ? 'the request is still processing.' : 'the report could not be completed.')}`).join(' ')}`
          : freshRequest ? 'New credit report request completed. The prior reports remain available below. Existing creditors were not changed.' : 'Credit report processing completed. Saved reports are available below.',
      });
      if (freshRequest && !failures.length && !pending.length) rememberPending(null);
      await loadReports();
    } catch (error) {
      setFeedback({ error: true, message: error.message });
      // A lost response does not establish whether the provider charged for a
      // report. Retain its request ID so a retry is the same operation.
      await loadReports();
    } finally {
      inFlight.current = false;
      setIsSubmitting(false);
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (inFlight.current) return;
    // Validate SSN fields match and correct format
    const ssnPattern = /^\d{3}-\d{2}-\d{4}$/;
    if (!ssnPattern.test(primary.ssn) || primary.ssn !== primary.ssnConfirm) {
      setFeedback({ error: true, message: 'Debtor 1 SSN entries must match and be in XXX-XX-XXXX format.' });
      return;
    }
    if (joint) {
      if (!ssnPattern.test(joint.ssn) || joint.ssn !== joint.ssnConfirm) {
        setFeedback({ error: true, message: 'Debtor 2 SSN entries must match and be in XXX-XX-XXXX format.' });
        return;
      }
    }
    runReport({ payload: { primary, joint }, saveInformation: true });
  };

  const hasSavedReport = role => reports.some(report =>
    String(report.debtor?.id ?? report.debtor ?? report.debtor_id) === String(savedPeople.current[role]?.id)
    || report.role === role,
  );

  const refreshSavedReports = () => {
    const payload = {};
    ['primary', 'joint'].forEach(role => {
      if (savedPeople.current[role] && hasSavedReport(role)) payload[role] = savedPeople.current[role];
    });
    if (!Object.keys(payload).length) {
      setFeedback({ error: true, message: 'No saved report is available to refresh for the filing debtors.' });
      return;
    }
    runReport({ payload });
  };

  const selectedPeople = scope => {
    const payload = {};
    if (scope !== 'joint') payload.primary = savedPeople.current.primary;
    if (scope !== 'primary' && savedPeople.current.joint) payload.joint = savedPeople.current.joint;
    return payload;
  };

  const reviewNewReport = (separate = false) => {
    if (inFlight.current) return;
    if (pendingFresh && !separate) {
      setFreshReview({ ...pendingFresh, payload: selectedPeople(pendingFresh.scope) });
      return;
    }
    setFreshReview({ scope: freshScope, payload: selectedPeople(freshScope), separate: Boolean(pendingFresh) });
  };

  const confirmNewReport = () => {
    if (!freshReview || inFlight.current) return;
    let request;
    try {
      request = { ...freshReview, id: freshReview.id || newRequestId() };
    } catch (error) {
      setFreshReview(null);
      setFeedback({ error: true, message: error.message });
      return;
    }
    runReport({
      payload: { ...request.payload, force_new: true, request_id: request.id },
      freshRequest: request,
    });
  };

  if (loading) return <p className="p-4">Loading...</p>;
  if (loadError) return <p role="alert" className="p-4 text-red-700">{loadError}</p>;

  return (
    <div className="max-w-3xl mx-auto space-y-6 p-1 text-left">
      <h2 className="text-xl font-bold">Credit reports</h2>
      {feedback && <p role={feedback.error ? 'alert' : 'status'} className={`rounded border p-3 ${feedback.error ? 'border-amber-300 bg-amber-50 text-amber-950' : 'border-green-300 bg-green-50 text-green-900'}`}>{feedback.message}</p>}
      {isSubmitting && <p role="status" className="rounded bg-blue-50 p-3 text-blue-900">Processing credit reports. Please wait; do not start another request or refresh the browser.</p>}
      <section aria-labelledby="saved-credit-reports" className="rounded-lg border bg-white p-4">
        <h3 id="saved-credit-reports" className="text-lg font-semibold">Saved report history</h3>
        <p className="mt-1 text-sm text-gray-600">New reports are saved separately. Prior reports stay available.</p>
        {reportsLoading && <p className="mt-3">Checking for saved reports...</p>}
        {reportsError && <div role="alert" className="mt-3 text-red-700">{reportsError} <button type="button" disabled={isSubmitting || reportsLoading} onClick={loadReports} className="underline disabled:opacity-50">Retry report history</button></div>}
        {!reportsLoading && !reports.length && !reportsError && <p className="mt-3 text-gray-600">No reports have been saved yet.</p>}
        <ul className="my-4 divide-y">
          {reports.map((report, index) => {
            const person = String(report.debtor?.id ?? report.debtor ?? report.debtor_id) === String(primary.id) || report.role === 'primary' ? primary : joint;
            return <li key={report.id || report.report_id || index} className="py-3">
              <p className="text-sm font-medium">{person ? `${person === primary ? 'Debtor 1' : 'Debtor 2'} · ${personName(person)}` : 'Credit report'} · <time dateTime={report.created_at || undefined}>{reportDate(report.created_at)}</time></p>
              {report.upload_url ? <a href={report.upload_url} target="_blank" rel="noopener noreferrer" className="break-words text-indigo-600 underline">Download {report.filename || 'credit report'}</a> : <span className="text-sm text-gray-600">PDF download unavailable</span>}
            </li>;
          })}
        </ul>
        <div className="flex flex-wrap gap-3">
          <a href={`/case/${caseId}/creditors`} className="rounded border px-4 py-2 text-indigo-700">Go to Creditors</a>
          {reports.length > 0 && <button type="button" onClick={refreshSavedReports} disabled={isSubmitting || reportsLoading} className="rounded border px-4 py-2 text-indigo-700 disabled:opacity-50">Refresh creditors from saved report</button>}
        </div>
        {reports.length > 0 && <p className="mt-2 text-sm text-gray-600">Refreshing creditors re-imports saved report data; it does not request updated credit information.</p>}
      </section>
      <section aria-labelledby="new-credit-report" className="rounded-lg border border-indigo-200 bg-indigo-50 p-4">
        <h3 id="new-credit-report" className="text-lg font-semibold">Run new credit report</h3>
        <p className="mt-1 text-sm text-gray-700">Request current information from OneView, rather than re-importing a saved report. A new report may incur a charge and a new credit inquiry.</p>
        <p className="mt-2 text-sm text-gray-700">Saves a new report without changing existing creditors. Use “Refresh creditors from saved report” separately if you want to import its information.</p>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          {joint && <label className="block text-sm font-medium">Who needs a new report?
            <select value={freshScope} onChange={event => setFreshScope(event.target.value)} disabled={isSubmitting} className="mt-1 block rounded border bg-white p-2">
              <option value="primary">Debtor 1 · {personName(primary)}</option>
              <option value="joint">Debtor 2 · {personName(joint)}</option>
              <option value="both">Both filing debtors</option>
            </select>
          </label>}
          <button type="button" onClick={() => reviewNewReport()} disabled={isSubmitting || reportsLoading || !primary.id} className="rounded bg-indigo-600 px-4 py-2 text-white disabled:opacity-50">{pendingFresh ? 'Check/retry same request' : 'Run new credit report'}</button>
          {pendingFresh && <button type="button" onClick={() => reviewNewReport(true)} disabled={isSubmitting || reportsLoading} className="rounded border px-4 py-2 text-indigo-700 disabled:opacity-50">Start a separate new request</button>}
        </div>
        {pendingFresh && <p className="mt-2 text-sm text-amber-900">The last request has not been confirmed. Retry the same request to check its result without authorizing another new report.</p>}
      </section>
      {freshReview && <section role="dialog" aria-modal="true" aria-labelledby="confirm-new-report" className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-gray-900/70 p-4">
        <div className="w-full max-w-lg rounded-lg bg-white p-5 shadow-xl">
          <h3 id="confirm-new-report" className="text-lg font-semibold">{freshReview.id ? 'Retry this report request?' : 'Confirm new credit report'}</h3>
          <p className="mt-3">{freshReview.scope === 'both' ? 'Both filing debtors' : freshReview.scope === 'joint' ? `Debtor 2 · ${personName(joint)}` : `Debtor 1 · ${personName(primary)}`}</p>
          <p className="mt-3 text-sm">{freshReview.id ? 'This checks or retries the same request. It does not authorize a separate new report.' : 'This contacts OneView for a new credit report and may incur a charge and a new credit inquiry. Existing reports will not be deleted. Continue only if authorized.'}</p>
          {freshReview.separate && <p className="mt-3 rounded bg-amber-50 p-3 text-sm text-amber-950">A previous request is still unresolved. This authorizes a separate new report and may charge again, even for a debtor whose report already completed.</p>}
          <div className="mt-5 flex flex-wrap justify-end gap-3">
            <button type="button" onClick={() => setFreshReview(null)} className="rounded border px-4 py-2">Cancel</button>
            <button type="button" onClick={confirmNewReport} className="rounded bg-indigo-600 px-4 py-2 text-white">{freshReview.id ? 'Retry same request' : 'Confirm and run new report'}</button>
          </div>
        </div>
      </section>}
      <details open={!reports.length} className="rounded-lg border bg-white p-4">
      <summary className="cursor-pointer font-semibold">Review debtor information / initial report</summary>
      <p className="my-3 text-sm text-gray-600">Submitting this form saves debtor information, then processes reports. If a report is already saved, it reuses that data. Use “Run new credit report” above for updated information.</p>
      <form onSubmit={handleSubmit} className="space-y-8">
        <fieldset disabled={isSubmitting} className="space-y-8 disabled:opacity-60">
        {['primary', 'joint'].map(key => {
          const person = key === 'primary' ? primary : joint;
          if (key === 'joint' && !joint) return null;
          return (
            <fieldset key={key} className="border rounded p-1">
              <legend className="font-semibold text-lg mb-2">
                {key === 'primary' ? 'Debtor 1' : 'Debtor 2'}
              </legend>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-1">
                <div>
                  <label className="block text-sm font-medium">First Name</label>
                  <input
                    type="text"
                    value={person.firstName}
                    onChange={handlePersonChange(key, 'firstName')}
                    className="mt-1 block w-full border rounded p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium">Middle Name</label>
                  <input
                    type="text"
                    value={person.middleName}
                    onChange={handlePersonChange(key, 'middleName')}
                    className="mt-1 block w-full border rounded p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium">Last Name</label>
                  <input
                    type="text"
                    value={person.lastName}
                    onChange={handlePersonChange(key, 'lastName')}
                    className="mt-1 block w-full border rounded p-2"
                  />
                </div>
              </div>

              <div className="mt-4">
                <h4 className="font-medium">Previous Names (last 8 years)</h4>
                {person.previousNames.map((alias, idx) => (
                  <div key={idx} className="grid grid-cols-1 sm:grid-cols-4 gap-0 mt-2 items-end">
                    <div>
                      <label className="block text-xs">First</label>
                      <input
                        type="text"
                        value={alias.first}
                        onChange={handleAliasChange(key, idx, 'first')}
                        className="mt-1 block w-full border rounded p-1 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs">Middle</label>
                      <input
                        type="text"
                        value={alias.middle}
                        onChange={handleAliasChange(key, idx, 'middle')}
                        className="mt-1 block w-full border rounded p-1 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs">Last</label>
                      <input
                        type="text"
                        value={alias.last}
                        onChange={handleAliasChange(key, idx, 'last')}
                        className="mt-1 block w-full border rounded p-1 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs">Type</label>
                      <select
                        value={alias.aliasType}
                        onChange={handleAliasChange(key, idx, 'aliasType')}
                        className="mt-1 block w-full border rounded p-1 text-sm"
                      >
                        {ALIAS_TYPE_CHOICES.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAlias(key, idx)}
                      className="text-red-600 text-sm mt-1"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addAlias(key)}
                  className="mt-2 inline-block text-indigo-600 text-sm underline"
                >
                  + Add previous name
                </button>
              </div>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium">SSN</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={11}
                    value={person.ssn}
                    onChange={handleSSNChange(key)}
                    placeholder="XXX-XX-XXXX"
                    className="mt-1 block w-full border rounded p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium">Confirm SSN</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={11}
                    value={person.ssnConfirm}
                    onChange={handleSSNConfirmChange(key)}
                    placeholder="XXX-XX-XXXX"
                    className="mt-1 block w-full border rounded p-2"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium">Address</label>
                  <div className="grid grid-cols-8 gap-2">
                    <input
                      type="text"
                      value={person.address.houseNumber}
                      onChange={handleAddressChange(key, 'houseNumber')}
                      placeholder="House #"
                      className="border rounded p-1 text-sm col-span-2"
                    />
                    <input
                      type="text"
                      value={person.address.streetName}
                      onChange={handleAddressChange(key, 'streetName')}
                      placeholder="Street Name"
                      className="border rounded p-1 text-sm col-span-4"
                    />
                    <input
                      type="text"
                      value={person.address.streetType}
                      onChange={handleAddressChange(key, 'streetType')}
                      placeholder="Type (Rd, St, etc)"
                      className="border rounded p-1 text-sm col-span-2"
                    />
                  </div>
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    <input
                      type="text"
                      value={person.address.city}
                      onChange={handleAddressChange(key, 'city')}
                      placeholder="City"
                      className="border rounded p-2 text-sm col-span-2"
                    />
                    <input
                      type="text"
                      value={person.address.state}
                      onChange={handleAddressChange(key, 'state')}
                      placeholder="State"
                      className="border rounded p-2 text-sm col-span-1"
                    />
                    <input
                      type="text"
                      value={person.address.zip}
                      onChange={handleAddressChange(key, 'zip')}
                      placeholder="ZIP"
                      className="border rounded p-2 text-sm col-span-1"
                    />
                  </div>
                </div>
              </div>
            </fieldset>
          );
        })}

        <div className="pt-4">
          <button
            type="submit"
            disabled={isSubmitting || reportsLoading || Boolean(reportsError)}
            className="px-6 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          >
            Save information and process reports
          </button>
        </div>
        </fieldset>
      </form>
      </details>
    </div>
  );
}
